/** WF-01 ticket statuses. Board columns are fixed and appear in this order. */
export const TICKET_STATUSES = ['pending', 'todo', 'in_progress', 'pending_release', 'closed'] as const;
export type TicketStatus = (typeof TICKET_STATUSES)[number];

export const STATUS_LABELS: Record<TicketStatus, string> = {
  pending: 'Pending',
  todo: 'Todo',
  in_progress: 'In progress',
  pending_release: 'Pending release',
  closed: 'Closed',
};

export const RESOLUTIONS = ['done', 'rejected'] as const;
export type Resolution = (typeof RESOLUTIONS)[number];

export const TICKET_TYPES = ['issue', 'feature_request'] as const;
export type TicketType = (typeof TICKET_TYPES)[number];

export const TYPE_LABELS: Record<TicketType, string> = { issue: 'Issue', feature_request: 'Feature request' };

/** TKT-04 proposed default priorities; `normal` on creation. */
export const PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
export type Priority = (typeof PRIORITIES)[number];
export const DEFAULT_PRIORITY: Priority = 'normal';

export const ROLES = ['user', 'admin'] as const;
export type Role = (typeof ROLES)[number];

export const CONTEXT_SECTIONS = ['overview', 'structure', 'deployment'] as const;
export type ContextSection = (typeof CONTEXT_SECTIONS)[number];
export const CONTEXT_SECTION_TITLES: Record<ContextSection, string> = {
  overview: 'Overview',
  structure: 'Project structure',
  deployment: 'Deployment instructions',
};

/** MCP-02 per-project AI write capabilities. */
export const AI_CAPABILITIES = [
  'ticket_create',
  'discussion',
  'approval_request',
  'implementation',
  'release_prepare',
  'delivery_execute',
] as const;
export type AiCapability = (typeof AI_CAPABILITIES)[number];
export const AI_CAPABILITY_LABELS: Record<AiCapability, string> = {
  ticket_create: 'Ticket creation',
  discussion: 'Discussion and attachment writes',
  approval_request: 'Approval requests',
  implementation: 'Implementation claims, progress, evidence, and readiness',
  release_prepare: 'Release/rollback preparation and approval requests',
  delivery_execute: 'Approved delivery execution registration and reporting',
};

/** client_secret: admin-issued client credentials; oauth_grant: a user's browser sign-in from an agent. */
export const AI_CREDENTIAL_KINDS = ['client_secret', 'oauth_grant'] as const;
export type AiCredentialKind = (typeof AI_CREDENTIAL_KINDS)[number];

/** WF-06 approval request intents and decision states. */
export const APPROVAL_INTENTS = ['acceptance', 'release', 'rollback'] as const;
export type ApprovalIntent = (typeof APPROVAL_INTENTS)[number];
export const APPROVAL_STATES = ['pending', 'approved', 'denied', 'revoked', 'expired', 'stale'] as const;
export type ApprovalState = (typeof APPROVAL_STATES)[number];

/** WF-11 release lifecycle. */
export const RELEASE_STATES = [
  'draft',
  'awaiting_approval',
  'approved',
  'deploying',
  'unknown',
  'failed',
  'succeeded',
  'cancelled',
] as const;
export type ReleaseState = (typeof RELEASE_STATES)[number];
/** Releases in these states reserve their tickets (WF-11). */
export const RESERVING_RELEASE_STATES: readonly ReleaseState[] = [
  'draft',
  'awaiting_approval',
  'approved',
  'deploying',
  'unknown',
  'failed',
];
/** Execution is unresolved in these states; bindings and included scopes are locked (WF-03, WF-12). */
export const UNRESOLVED_EXECUTION_STATES: readonly ReleaseState[] = ['deploying', 'unknown'];

export const ATTEMPT_STATES = ['deploying', 'unknown', 'failed', 'succeeded'] as const;
export type AttemptState = (typeof ATTEMPT_STATES)[number];
export const DEPLOYMENT_OUTCOMES = ['success', 'failure', 'unknown'] as const;
export type DeploymentOutcome = (typeof DEPLOYMENT_OUTCOMES)[number];

export const ROLLBACK_STATES = RELEASE_STATES;
export type RollbackState = ReleaseState;

export const IMPLEMENTATION_STATES = [
  'active',
  'stale',
  'ready',
  'released',
  'recovered',
  'terminated',
] as const;
export type ImplementationState = (typeof IMPLEMENTATION_STATES)[number];
export const IMPLEMENTATION_OWNER_KINDS = ['ai', 'human'] as const;
export type ImplementationOwnerKind = (typeof IMPLEMENTATION_OWNER_KINDS)[number];

export const SCAN_STATES = ['pending', 'clean', 'quarantined', 'removed'] as const;
export type ScanState = (typeof SCAN_STATES)[number];

export const CRITERION_OUTCOMES = ['met', 'not_met', 'not_applicable'] as const;
export type CriterionOutcome = (typeof CRITERION_OUTCOMES)[number];

export const TEST_RESULTS = ['passed', 'failed', 'not_run'] as const;
export type TestResult = (typeof TEST_RESULTS)[number];

/** reported: by the executor; verified: by an admin; webhook: from the Deploy run's GitHub event. */
export const PROVENANCE = ['reported', 'verified', 'webhook'] as const;
export type Provenance = (typeof PROVENANCE)[number];

/** Where an action came from. 'mcp' is the retired MCP transport, kept for history (the audit ledger is append-only). */
export const ACTION_SOURCES = ['ui', 'agent', 'mcp', 'system'] as const;
export type ActionSource = (typeof ACTION_SOURCES)[number];
